/* boolean.control_adapter.js */

"use strict";

const _ = require("lodash");
const underscoreString = require("underscore.string");
const CheckBox = require("@gluenetworks/input_checkbox_control");

_.mixin(underscoreString.exports());
const EventEmitter = require("events").EventEmitter;
const util = require("util");
const q = require("q");
const StyleUtil = require("../StyleUtil");

/**
 * @event BooleanControlAdapter#ValueChanged
 * @type {Object}
 * @property oldValue {string} The previous value
 * @property newValue {string} The new value
 */

/**
 * @class BooleanControlAdapter
 * @classdesc renders the Select Options Jtype.
 * @implements AngularControlAdapter
 *
 * @param logger {Bunyan} The logger
 * @param containingElem {DomElement} The containing dom element for this control
 * @param formCtrlDef {FormControlDefinition} The definition of this control object.
 *
 * @requires util
 * @requires EventEmitter
 *
 * @fires BooleanControlAdapter#ValueChanged
 */
module.exports = function BooleanAdapter(logger, containingElem, formCtrlDef) {
    let self = this;
    let loadDeferred = q.defer();
    //call Event Emitter Constructor
    EventEmitter.call(this);
    this._multiEditMode = _.get(formCtrlDef, "displayModeOptions.multiEditMode", false);
    let _scope;
    let _oldValue;

    /********************************************************************************/
    /*** START Type Definitions                                                   ***/
    /********************************************************************************/

    /********************************************************************************/
    /*** END Type Definitions                                                     ***/
    /*** START Private API                                                        ***/
    /********************************************************************************/

    /**
     * Fire the Value Changed Event
     * @param silent {boolean} If true will not emit the ValueChanged event.
     * @private
     */
    function _fireValueChanged(silent) {
        //Get the new Value
        let newVal = self.getValue();
        if (!silent) {
            self.emit("ValueChanged", {
                name: formCtrlDef.name,
                containingElem: containingElem,
                controlAdapter: self,
                formControlDefinition: formCtrlDef,
                oldValue: _oldValue,
                newValue: newVal,
            });
        }
        _oldValue = newVal;
    }

    /********************************************************************************/
    /*** END Private API                                                          ***/
    /*** START Public API                                                         ***/
    /********************************************************************************/
    //The name of the control
    this.name = formCtrlDef.name;

    /**
     * @returns the data associated with this control.
     */
    this.getValue = function () {
        let val = formCtrlDef.value;
        if (this.checkBox.getValue) {
            val = this.checkBox.getValue();
        }
        return val;
    };

    /**
     * Set this controls current value.
     * @param value {string} The value to set this control to.
     * @returns the value associated with this control, it might not always be the same as the value passed in for
     * example adapters with a ValueScope.PARENT.
     */
    this.setValue = function (value) {
        if (this.checkBox.setValue) {
            this.checkBox.setValue(value);
            _fireValueChanged(true);
        }
        return self.getValue();
    };

    this.getMultiEditActive = function () {
        return this._multiEditActive;
    };

    this.setMultiEditActive = function (flag) {
        if (!this._readOnlyField) {
            this.setReadOnly(!flag);
            let oldValue = this._multiEditActive;
            this._multiEditActive = flag;
            if (oldValue !== this._multiEditActive) {
                this.emit("MultiEditActive", {
                    oldValue: oldValue,
                    newValue: this._multiEditActive,
                });
            }
        }
    };

    /**
     * Tear down this control
     */
    this.tearDown = function () {
        //Do Nothing the dom is cleaned up, the scope is destroyed for us and any listeners
        //on self are unregistered automatically
    };

    /********************************************************************************/
    /*** END Public API                                                           ***/
    /*** Start Angular/Render Work                                                ***/
    /********************************************************************************/

    /**
     * A Hook to start the angular work, due to timing issues don't try anything angularis before this hook is called.
     * @param $scope {$scope} Angular $scope
     * @parma $templateCache {$templateCache}
     */
    this.$init = function ($scope) {
        _scope = $scope;

        _scope.formCtrlDef = formCtrlDef;

        let options = {
            labelShown: true,
            label: formCtrlDef.displayInfo.label,
            title: formCtrlDef.displayInfo.tooltip,
        };
        this.checkBox = new CheckBox(options);
        let initialValue = formCtrlDef.value;
        if (this._multiEditMode) {
            initialValue = false;
        }
        this.checkBox.setValue(initialValue);
        this.checkBox.on("Value", (this._valueChanged = this._valueChanged.bind(this)));

        let stylesConfig = _.get(formCtrlDef, "configSettings.Display.styles", {});
        StyleUtil.renderStylesConfig(stylesConfig, containingElem[0]);

        // Apply item layout styles - these styles customize how this boolean element is placed in parent layout
        let itemLayoutCssClass = _.get($scope, "jTypeContext.layout.item.cssClass", "");
        containingElem.addClass(itemLayoutCssClass);

        //Setup directive
        containingElem.append(this.checkBox.renderer.rootElement);

        //Notify the world this adapter is ready to be compiled/linked and values retrieved
        self.emit("InitializationComplete", {
            initializedValue: self.getValue(),
        });

        return loadDeferred.promise;
    };
    this.$init.$inject = ["$scope", "$templateCache"];

    /**
     * A Hook for when this control has been compiled/Linked.
     * @param $scope {$scope} The Angular Scope variable
     * @param $timeout The angular timeout object
     *
     * if $compiledAndLinked.$inject is specified those injection parameters will be used as the arguments
     */
    this.$compiledAndLinked = function ($scope, $timeout) {
        let configData;
        if (_.isBoolean(_.get(formCtrlDef, "configData"))) {
            configData = _.get(formCtrlDef, "configData");
        } else if (!_.isUndefined(_.get(formCtrlDef, "configData.value", undefined))) {
            configData = _.get(formCtrlDef, "configData.value");
        }

        if (configData) {
            // Update used/available based on configData
            self.setValue(configData);
            $timeout(function () {
                loadDeferred.resolve();
            });
        } else {
            loadDeferred.resolve();
        }

        if (_.get(formCtrlDef, "configSettings.Display.readOnly")) {
            this._readOnlyField = true;
            this.setReadOnly(true);
        }

        if (_.get(formCtrlDef, "configSettings.Display.labelFirst", false)) {
            this.setLabelFirst(true);
        }

        // If this is multiEditMode, set the field as read only by default
        if (this._multiEditMode) {
            this.setReadOnly(true);
        }
    };
    this.$compiledAndLinked.$inject = ["$scope", "$timeout"];

    this._valueChanged = function () {
        _fireValueChanged();
    };

    this.setReadOnly = function (flag) {
        this.checkBox.setDisabled(flag);
    };

    this.setLabelFirst = function (flag) {
        this.checkBox.setLabelFirst(flag);
    };
    /********************************************************************************/
    /*** END Angular/Render Work                                                  ***/
    /********************************************************************************/
};
//Make this API inherit from EventEmitter
util.inherits(module.exports, EventEmitter);

//Tell the Form renderer that this is an Angular Control.
module.exports.isAngularControl = true;

//Tell the Form renderer that this adapter supports multi edit mode.
module.exports.isMultiEditSupported = true;
